import React, { useState } from 'react';
import cs from 'classnames';
import { FourPartAddress } from './FourPartAddress';
import { addressJoin, AddressSegments, defaultAddressSegments } from './segments';
import styles from 'shared/address/AddressTypeahead.module.scss';
import { useField, useFormikContext } from 'formik';
import { getFormikFieldServerErrors } from 'shared/forms/helpers';
import { FieldNames, getAddressFieldNames } from './utils';
import fieldStyles from './AddressField.module.scss';
import { CustomOption } from 'react-bootstrap-typeahead';
import { useValidateAddressMutation } from '../../generated.graphql';
import { AddressTypeaheadEquifax } from 'shared/address/AddressTypeaheadEquifax';


export type AddressComponentType = 'label' | 'typeahead' | 'custom';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  fieldPrefix: string;
  addressName?: string | 'residential' | 'delivery';
  placeholderText?: string;
  error?: string;
  //onChanged: (address: AddressType) => void;
}

type AddressType = string | AddressSegments | null;

function isAddressString(address: AddressType): address is string {
  return address !== null && (address as string).length !== undefined;
}

function isAddressSegments(address: AddressType): address is AddressSegments {
  return address !== null && (address as AddressSegments).street !== undefined;
}

function isCustomOption(value: string | CustomOption): value is CustomOption {
  return (value as CustomOption).label !== undefined;
}

/**
 * A component that uses a parent formik context to set fields as follows:
 * 
 * e.g. props.fieldPrefix = "residential_";
 * 
 * formik.fields = {
 *   residential_addressComponent: 'label' | 'typeahead' | 'custom';
 *   residential_fullAddress: string;
 *   residential_street: string;
 *   residential_suburb: string;
 *   residential_state: string;
 *   residential_postcode: string;
 * }
 * 
 * A validation schema could be applied as follows:
 * 
 * yup.object().shape({
 *   residential_addressComponent: yup.string().oneOf(['label', 'typeahead', 'custom']),
 *   residential_fullAddress: yup.string().required(),
 *   residential_street: yup.string().when('residential_addressComponent', {
 *     is: 'custom',
 *     then: yup.string().min(5, 'Must be a full street address.  Eg. 123 Example Road').max(255).required()
 *   }),
 *   residential_suburb: yup.string().when('residential_addressComponent', {
 *     is: 'custom',
 *     then: yup.string().max(100).required()
 *   }),
 *   residential_state: yup.string().when('residential_addressComponent', {
 *     is: 'custom',
 *     then: yup.string().oneOf(stateOptions.map(o => o[0])).required()
 *   }),
 *   residential_postcode: yup.string().when('residential_addressComponent', {
 *     is: 'custom',
 *     then: yup.string().matches(/\d{4}/, 'Must be a 4 digit postcode').required()
 *   })
 * })
 */
export const AddressField = ({fieldPrefix, addressName, placeholderText, ...props}: Props) => {
  const [ typeaheadAddress, setTypeaheadAddress ] = useState('');
  addressName = addressName ?? 'residential';

  const fieldNames = getAddressFieldNames(fieldPrefix);
  const [addressComponentField] = useField(fieldNames.addressComponent);
  const [fullAddressField, fullAddressMeta] = useField(fieldNames.fullAddress);
  const showError = !!props.error || !!(fullAddressMeta.touched && fullAddressMeta.error);
  const context = useFormikContext();
  const serverErrors = getFormikFieldServerErrors(context.status, fullAddressField.name);

  const [, validateAddress] = useValidateAddressMutation();
  
  const setAddress = (value: AddressType) => {
    if (isAddressString(value)) {
      context.setFieldValue(fieldNames.addressComponent, 'label');
      context.setFieldValue(fieldNames.fullAddress, value);
    } else if (isAddressSegments(value)) {
      context.setFieldValue(fieldNames.addressComponent, 'custom');
      context.setFieldValue(fieldNames.fullAddress, addressJoin(value));
      Object.keys(value)
        .map<[keyof AddressSegments, keyof FieldNames]>(name => [name as keyof AddressSegments, name as keyof FieldNames])
        .forEach(([k1, k2]) => context.setFieldValue(fieldNames[k2], value[k1]));
    } else {
      context.setFieldValue(fieldNames.addressComponent, 'typeahead');
      context.setFieldValue(fieldNames.fullAddress, null);
    }

    // if we didn't set the address to address segments, wipe out the custom address fields
    if (!isAddressSegments(value)) {
      Object.keys(defaultAddressSegments)
        .map<keyof FieldNames>(name => name as keyof FieldNames)
        .forEach(name => context.setFieldValue(fieldNames[name], null));
    }
  }

  const setDefaultAddress = () => {
    setAddress({...defaultAddressSegments});
  }

  const setAddressFromText = async (addressText: string) => {
    if (addressText.trim().length > 0) {

      const r = await validateAddress({input:{address: addressText}});
      const a = r.data?.validateAddress?.address;
      setAddress({
        street: a?.street || '',
        suburb: a?.suburb || '',
        state: a?.state || '',
        postcode: a?.postcode || '',
      });

    } else {
      setDefaultAddress();
    }
  }

  const _handleTypeaheadInputChange = (value: string, isCustom: boolean) => {
    setTypeaheadAddress(value);
  };

  const _handleTypeaheadSelect = (value: string | CustomOption) => {
    if (isCustomOption(value)) {
      setAddressFromText(value.label);
    } else {
      setAddress(value);
    }
  }

  // const _handleCustomAddressChange = (value: AddressSegments) => {
  //   setAddress(value);
  // };

  const _handleTypeaheadBlur = () => {
    if (typeaheadAddress.trim()) {
      setAddressFromText(typeaheadAddress);
    }
  };


  const enterAuto = (
    <button type="button" className="btn btn-link d-inline-block pt-1 mt-md-2 mb-4" onClick={(e) => { setAddress(null); }}>
      Enter {addressName} address automatically
    </button>
  );

  const enterManual = (
    <button type="button" className="btn btn-link d-inline-block pt-1 mt-md-2 mb-4" onClick={(e) => { setAddressFromText(typeaheadAddress); }}>
      Enter {addressName} address manually
    </button>
  );

  if (addressComponentField.value === 'label') {
    return (
      <div className="form-row">
        <div className="col-md-8 col-lg-9">
          <div
            className={cs("form-control d-flex", fieldStyles.AddressLabel, (showError || !!serverErrors) && "is-invalid")}
          >
            <span className={styles.simpleAddressText}>{fullAddressField.value}</span>
            <button type="button" className={cs(styles.simpleAddressButton, "btn btn-sm btn-link")} onClick={() => {
              setAddress(null);
            }}>Change</button>
          </div>
          {showError && (
            <div className="invalid-feedback">{props.error || fullAddressMeta.error}</div>
          )}
        </div>
        <div className="col-md-4 col-lg-3 text-md-right">
          {enterManual}
        </div>
      </div>
    );
  }

  if (addressComponentField.value === 'custom') {
    return (
      <FourPartAddress fieldNames={fieldNames} error={props.error}>
        <div className="col-md-5 text-md-right">
          {enterAuto}
        </div>
      </FourPartAddress>
    );
  }

  return (
    <div className="form-row">
      <div className="col-md-8 col-lg-9">
        <div className="form-group mb-0">
          <AddressTypeaheadEquifax
            name={fullAddressField.name}
            onInputChange={_handleTypeaheadInputChange}
            onBlur={_handleTypeaheadBlur}
            onSelect={_handleTypeaheadSelect}
            placeholder={placeholderText}
            isInvalid={showError || !!serverErrors}
          />
          {showError && (
            <div className="text-danger small mt-1 mb-3">{props.error || fullAddressMeta.error}</div>
          )}
        </div>
      </div>
      <div className="col-md-4 col-lg-3 text-md-right">
        {enterManual}
      </div>
    </div>
  );
}